import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { api } from "../../utils/api";
import { authStore } from "../../store/authStore";
import {
  getUserPhotoKey,
  getUserPhotoSnapshot,
  invalidateUserPhoto,
  loadUserPhoto,
  subscribeUserPhoto,
} from "../../utils/userPhotoCache";
import "./UserAvatar.css";

export const notifyUserPhotoUpdated = (userId, companyId) => {
  invalidateUserPhoto(getUserPhotoKey(userId, companyId));
};

// Keep support for JSON/base64 responses as well as image file responses.
const getImageSource = (result) => {
  const data = result?.data ?? result;
  const image = typeof data === "string"
    ? data
    : data?.image_base64 ?? data?.image ?? data?.base64;
  if (typeof image !== "string" || !image) return "";
  if (image.startsWith("data:image/")) return image;
  const mimeType = data?.mime_type || data?.content_type || "image/png";
  return `data:${mimeType};base64,${image}`;
};

export default function UserAvatar({ userId, companyId, name, preview, large = false }) {
  const [failedSource, setFailedSource] = useState(null);
  const key = getUserPhotoKey(userId, companyId);
  const subscribe = useCallback((listener) => subscribeUserPhoto(key, listener), [key]);
  const getSnapshot = useCallback(() => getUserPhotoSnapshot(key), [key]);
  const photo = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!key || preview || !authStore.isAuthenticated()) return;
    loadUserPhoto(key, async (signal, reload) => {
      const response = await api.get(`/user/image/${userId}/${companyId}`, {
        signal,
        cache: reload ? "reload" : "no-cache",
      });
      const contentType = response.headers.get("Content-Type")?.toLowerCase() || "";
      if (contentType.startsWith("image/") || contentType.startsWith("application/octet-stream")) {
        const blob = await response.blob();
        return signal.aborted ? "" : URL.createObjectURL(blob);
      }
      const result = await response.json();
      return signal.aborted ? "" : getImageSource(result);
    });
  }, [key, userId, companyId, preview, photo.status]);

  const source = preview || photo.source;
  const loading = Boolean(key && !preview && (photo.status === "idle" || photo.status === "loading"));
  const initials = name?.trim().split(/\s+/).slice(0, 2)
    .map((part) => part[0]).join("").toUpperCase() || "?";

  return (
    <span className={`user-avatar${large ? " user-avatar-large" : ""}`} aria-busy={loading}>
      {source && source !== failedSource ? (
        <img
          src={source}
          alt={`${name?.trim() || "User"} avatar`}
          onError={() => setFailedSource(source)}
        />
      ) : loading ? (
        <span className="user-avatar-placeholder" role="status" aria-label="Loading profile photo" />
      ) : (
        <span role="img" aria-label={`${name?.trim() || "User"} avatar`}>{initials}</span>
      )}
    </span>
  );
}
